import { useCallback } from 'react'
import tw, { TwStyle } from 'twin.macro'

import useElementScrollInfo from '@/utils/useElementScrollInfo'

type Directions = 'left' | 'right' | 'top' | 'bottom'
type DirectionsMap = {
  [key in Directions]?: boolean
}

type Corners = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'
type CornersMap = {
  [key in Corners]?: boolean
}

type IndicatorProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  position: Directions
  // eslint-disable-next-line react/no-unused-prop-types
  isVisible: boolean
}

type TwCss = TwStyle | TwStyle[]

export type OverflowIndicatorProps = {
  children: JSX.Element
  className?: string
  css?: TwCss | TwCss[]
  /** The scroll distance threshold to display the indicator */
  threshold?: number
  /** Extra css styles to add to the inner scroll container */
  scrollContainerCSS?: TwCss
  /** Extra css styles to add to the indicator elements */
  indicatorCSS?: TwCss | (({ position, isVisible }: IndicatorProps) => TwCss)
  disableOverflowX?: boolean
  disableOverflowY?: boolean
  /** The directions which to show the indicators. By default all directions are enabled */
  enabledDirections?: DirectionsMap
  /** Control which corners are rounded or not. Defaults to all rounded */
  roundedCorners?: boolean | CornersMap
  /** The background color of the container. Also adjusts the color of the indicator */
  bgColor?: null | 'primaryBlue' | 'indigoBlue' | 'lightGray' | 'darkGray' | 'white'
}

/** An overflow container that will automatically display visual indicators when there is overflow content in a given direction */
export default function OverflowIndicator({
  children,
  className,
  css,
  threshold = 4,
  scrollContainerCSS,
  indicatorCSS,
  disableOverflowX = false,
  disableOverflowY = false,
  enabledDirections = {
    top: true,
    bottom: true,
    left: true,
    right: true,
  },
  bgColor = null,
  roundedCorners = true,
}: OverflowIndicatorProps) {
  const [containerRef, { isScrollableX, isScrollableY, scrollLeft, scrollRight, scrollTop, scrollBottom }] =
    useElementScrollInfo()
  const shouldShowLeft = enabledDirections.left && isScrollableX && scrollLeft > threshold
  const shouldShowRight = enabledDirections.right && isScrollableX && scrollRight > threshold
  const shouldShowTop = enabledDirections.top && isScrollableY && scrollTop > threshold
  const shouldShowBottom = enabledDirections.bottom && isScrollableY && scrollBottom > threshold

  const rounded: CornersMap =
    typeof roundedCorners === 'boolean' && roundedCorners
      ? {
          topLeft: true,
          topRight: true,
          bottomLeft: true,
          bottomRight: true,
        }
      : {}

  const Indicator = useCallback(
    ({ position = 'left', isVisible = false }: IndicatorProps) => (
      <div
        css={[
          tw`absolute opacity-0 z-10 transition-opacity duration-200 ease-out pointer-events-none`,
          position === 'left' && tw`h-full w-8 top-0 left-0 bg-gradient-to-r`,
          position === 'left' && rounded.topLeft && tw`rounded-tl-[1.6rem]`,
          position === 'left' && rounded.bottomLeft && tw`rounded-bl-[1.6rem]`,
          position === 'right' && tw`h-full w-8 top-0 right-0 bg-gradient-to-l z-[11]`,
          position === 'right' && rounded.topRight && tw`rounded-tr-[1.6rem]`,
          position === 'right' && rounded.bottomRight && tw`rounded-br-[1.6rem]`,
          position === 'top' && tw`w-full h-8 top-0 bg-gradient-to-b`,
          position === 'top' && rounded.topLeft && tw`rounded-tl-[1.6rem]`,
          position === 'top' && rounded.topRight && tw`rounded-tr-[1.6rem]`,
          position === 'bottom' && tw`w-full h-8 bottom-0 bg-gradient-to-t`,
          position === 'bottom' && rounded.bottomLeft && tw`rounded-bl-[1.6rem]`,
          position === 'bottom' && rounded.bottomRight && tw`rounded-br-[1.6rem]`,
          bgColor === 'primaryBlue' && tw`from-indigoBlue`,
          bgColor === 'indigoBlue' && tw`from-black`,
          bgColor === 'lightGray' && tw`from-gray-500`,
          bgColor === 'darkGray' && tw`from-white`,
          bgColor === 'white' && tw`from-gray-500`,
          bgColor === null && tw`from-gray-500`,
          isVisible && bgColor === 'primaryBlue' && tw`opacity-30`,
          isVisible && bgColor === 'indigoBlue' && tw`opacity-30`,
          isVisible && bgColor === 'darkGray' && tw`opacity-30`,
          isVisible && bgColor === 'lightGray' && tw`opacity-30`,
          isVisible && bgColor === 'white' && tw`opacity-20`,
          isVisible && bgColor === null && tw`opacity-20`,
          typeof indicatorCSS === 'function' ? indicatorCSS?.({ position, isVisible }) : indicatorCSS,
        ]}
        data-testid={`overflow-indicator-${position}`}
      />
    ),
    [bgColor, roundedCorners, indicatorCSS]
  )

  return (
    <div
      className={className}
      css={[
        tw`relative overflow-hidden`,
        rounded.topLeft && tw`rounded-tl-3xl`,
        rounded.topRight && tw`rounded-tr-3xl`,
        rounded.bottomLeft && tw`rounded-bl-3xl`,
        rounded.bottomRight && tw`rounded-br-3xl`,
        bgColor === 'primaryBlue' && tw`bg-primaryBlue`,
        bgColor === 'indigoBlue' && tw`bg-indigoBlue`,
        bgColor === 'darkGray' && tw`bg-gray-800`,
        bgColor === 'lightGray' && tw`bg-gray-100`,
        bgColor === 'white' && tw`bg-white`,
        css,
      ]}
    >
      <div
        ref={containerRef}
        css={[
          tw`w-full h-full`,
          !disableOverflowX && !disableOverflowY && tw`overflow-auto`,
          !disableOverflowX && disableOverflowY && tw`overflow-x-auto overflow-y-clip`,
          disableOverflowX && !disableOverflowY && tw`overflow-y-auto overflow-x-clip`,
          scrollContainerCSS,
        ]}
        data-testid="overflow-indicator-scroll-container"
      >
        {enabledDirections.left && !disableOverflowX && <Indicator position="left" isVisible={shouldShowLeft} />}
        {enabledDirections.right && !disableOverflowX && <Indicator position="right" isVisible={shouldShowRight} />}
        {enabledDirections.top && !disableOverflowY && <Indicator position="top" isVisible={shouldShowTop} />}
        {enabledDirections.bottom && !disableOverflowY && <Indicator position="bottom" isVisible={shouldShowBottom} />}
        {children}
      </div>
    </div>
  )
}
